import { useEffect, useRef, useState } from "react";
import { api } from "../api";
import { G } from "./Shared";

// A type-ahead over people. Narrows as you type and offers full names, because
// nobody remembers an address — and it sends the id, so "every project this
// person represents" is an exact question rather than a name match.
//
// The list comes from /api/users/search, which applies the same scope as the
// user list: an operator sees their groups and every unaffiliated researcher.
// So this cannot become a way to enumerate another facility's staff.

const inputStyle = {
  background: "#fff", border: "1px solid #d1d5db", borderRadius: 8, color: "#1a1a1a",
  padding: "10px 13px", fontSize: 14, outline: "none", width: "100%", boxSizing: "border-box",
  fontFamily: "inherit",
};

export default function UserPicker({ value, valueLabel, onChange, placeholder, disabled, id,
                                     // `required` is deliberately swallowed rather than put on the
                                     // input: the text in the box is not the answer, the chosen id
                                     // is, so a browser that accepted any typed word would call the
                                     // field satisfied with nobody picked. Field also hands over
                                     // aria-required and aria-describedby, and those do belong on
                                     // the control — that is what announces the label's asterisk
                                     // and its hint.
                                     required, ...aria }) {
  const [term, setTerm]       = useState(valueLabel || "");
  const [people, setPeople]   = useState([]);
  const [open, setOpen]       = useState(false);
  const [active, setActive]   = useState(-1);
  const box = useRef(null);

  useEffect(() => { setTerm(valueLabel || ""); }, [valueLabel]);

  useEffect(() => {
    if (!open) return;
    // Debounced: a keystroke per request would put a query on the database for
    // every letter of every name anybody types.
    const timer = setTimeout(() => {
      api.get(`/api/users/search?q=${encodeURIComponent(term)}`)
        .then(setPeople).catch(() => setPeople([]));
    }, 200);
    return () => clearTimeout(timer);
  }, [term, open]);

  useEffect(() => {
    const away = e => { if (box.current && !box.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", away);
    return () => document.removeEventListener("mousedown", away);
  }, []);

  const choose = person => {
    onChange(person);
    setTerm(person ? (person.full_name || person.email) : "");
    setOpen(false);
    setActive(-1);
  };

  const onKeyDown = e => {
    if (!open) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setActive(a => Math.min(a + 1, people.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive(a => Math.max(a - 1, 0)); }
    else if (e.key === "Enter" && active >= 0) { e.preventDefault(); choose(people[active]); }
    else if (e.key === "Escape") { setOpen(false); }
  };

  return (
    <div ref={box} style={{ position: "relative" }}>
      <input
        id={id}
        value={term}
        disabled={disabled}
        placeholder={placeholder || "Start typing a name…"}
        autoComplete="off"
        {...aria}
        role="combobox"
        aria-expanded={open}
        // Required by the role: without it a screen reader announces a combobox
        // whose options it cannot find. The app targets WCAG 2.2 AA, so this is
        // a defect rather than a lint preference.
        aria-controls={`${id || "userpicker"}-listbox`}
        aria-autocomplete="list"
        onChange={e => { setTerm(e.target.value); setOpen(true); if (value) onChange(null); }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
        style={inputStyle}
      />
      {value && (
        <button type="button" onClick={() => choose(null)} aria-label="Clear selection"
          style={{ position: "absolute", right: 8, top: 8, border: "none", background: "none",
                   cursor: "pointer", color: "#6b7280", fontSize: 16 }}>×</button>
      )}
      {open && people.length > 0 && (
        <ul id={`${id || "userpicker"}-listbox`} role="listbox" style={{
          position: "absolute", zIndex: 40, top: "100%", left: 0, right: 0, margin: "4px 0 0",
          padding: 0, listStyle: "none", background: "#fff", border: "1px solid #d1d5db",
          borderRadius: 8, maxHeight: 240, overflowY: "auto", boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
        }}>
          {people.map((p, i) => (
            <li key={p.id} role="option" aria-selected={i === active}
                onMouseDown={() => choose(p)} onMouseEnter={() => setActive(i)}
                style={{ padding: "8px 12px", cursor: "pointer", fontSize: 13,
                         background: i === active ? "rgba(0,150,64,0.08)" : "transparent",
                         borderLeft: i === active ? `3px solid ${G}` : "3px solid transparent" }}>
              <div style={{ fontWeight: 600 }}>{p.full_name || p.email}</div>
              <div style={{ color: "#6b7280", fontSize: 12 }}>{p.email}</div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
